import { supabase } from '@/integrations/supabase/client';
import type { User } from '@supabase/supabase-js';
import type { Conversation } from '@/hooks/useConversations'; // Assuming Conversation type is defined here or moved to types/
import { ensureProfileExists } from './profileService'; // Import the moved function

// Define Partner type for clarity
interface PartnerInfo {
  id: string;
  display_name?: string;
  role?: string;
  avatar_url?: string | null;
  is_online?: boolean;
  is_verified?: boolean;
}

// Helper function to fetch partner details robustly
const fetchPartnerDetails = async (partnerId: string): Promise<Partial<PartnerInfo>> => {
  let details: Partial<PartnerInfo> = { id: partnerId };
  
  // 1. Try profiles table
  const { data: profileData, error: profileError } = await supabase
    .from('profiles')
    .select('id, display_name, role, avatar_url, is_verified')
    .eq('id', partnerId)
    .maybeSingle();
  
  if (profileError) console.warn(`[fetchPartnerDetails] Profile fetch error: ${profileError.message}`);
  
  if (profileData) {
    details = { ...details, ...profileData, is_online: false }; // Assume offline for non-escorts
  } else {
    // 2. Try escort_profiles table
    const { data: escortData, error: escortError } = await supabase
      .from('escort_profiles')
      .select('id, display_name, is_online, is_verified') // Removed fields not directly on table based on schema
      .eq('id', partnerId)
      .maybeSingle();
      
     if (escortError) console.warn(`[fetchPartnerDetails] Escort profile fetch error: ${escortError.message}`);
     
     if (escortData) {
       details = {
         ...details,
         display_name: escortData.display_name,
         role: 'escort',
         is_online: escortData.is_online ?? false,
         is_verified: escortData.is_verified,
         avatar_url: null // Fetch avatar next
       };
     } 
  }
  
  // 3. Fetch escort avatar if needed
  if (details.role === 'escort' && !details.avatar_url) {
      const { data: photoData, error: photoError } = await supabase
        .from('escort_photos')
        .select('photo_url')
        .eq('escort_id', partnerId)
        .eq('is_main', true)
        .maybeSingle();
        
      if (photoError) console.warn(`[fetchPartnerDetails] Photo fetch error: ${photoError.message}`);
      
      if (photoData?.photo_url) {
         const { data: urlData } = supabase.storage.from('escort-photos').getPublicUrl(photoData.photo_url);
         details.avatar_url = urlData?.publicUrl;
      }
  }
  
   // Final fallback / cleanup if name is missing
   if (!details.display_name) details.display_name = 'Utilisateur';
   if (details.role !== 'escort') details.is_online = false; // Ensure non-escorts are offline

  return details;
};


// Fetches conversations for the given user
export const fetchConversationsData = async (userId: string): Promise<Conversation[]> => {
  // Fetch base conversations first
  const { data: conversationsData, error: conversationsError } = await supabase
    .from('conversations')
    // Select base fields + last message content
    .select(`
      *,
      last_message:messages!conversations_last_message_id_fkey(content)
    `)
    .or(`participant1_id.eq.${userId},participant2_id.eq.${userId}`)
    .order('last_message_time', { ascending: false });

  if (conversationsError) throw conversationsError;
  if (!conversationsData) return [];

  // Process conversations: fetch partner details and unread counts in parallel
  const processedConversationsPromises = conversationsData.map(async (conv) => {
    const partnerId = conv.participant1_id === userId ? conv.participant2_id : conv.participant1_id;
    
    // Fetch partner details using the robust helper
    const partnerDetails = await fetchPartnerDetails(partnerId);
    
    // Fetch unread count for this conversation
    const { count, error: countError } = await supabase
      .from('messages')
      .select('id', { count: 'exact' })
      .eq('conversation_id', conv.id)
      .eq('recipient_id', userId)
      .eq('is_read', false);

    if (countError) {
      console.error(`Error getting unread count for conv ${conv.id}:`, countError);
    }
    
    // Assemble the final Conversation object
    return {
      id: conv.id,
      participant1_id: conv.participant1_id,
      participant2_id: conv.participant2_id,
      last_message: conv.last_message?.content || null,
      last_message_time: conv.last_message_time,
      status: conv.status,
      partner: {
        id: partnerId,
        name: partnerDetails.display_name || 'Utilisateur', // Use fetched name
        type: partnerDetails.role === 'escort' ? 'escort' : 'client', // Determine type
        avatar: partnerDetails.avatar_url || null, // Use fetched avatar
        isOnline: partnerDetails.is_online,
        isVerified: partnerDetails.is_verified,
      },
      unreadCount: count || 0,
    } as Conversation; // Assert type for safety
  });

  const results = await Promise.all(processedConversationsPromises);
  // Filter out potential nulls if fetchPartnerDetails could fail badly (though it shouldn't)
  return results.filter(conv => conv !== null);
};

// Starts a new conversation or returns an existing one
export const startNewConversation = async (userId: string, partnerId: string): Promise<Conversation | null> => {
  // 1. Ensure both profiles exist
  const partnerStatus = await ensureProfileExists(partnerId);
  const userStatus = await ensureProfileExists(userId);

  // User must exist in profiles table (or have been created)
  const canUserProceed = userStatus.existsInProfiles;
  // Partner must exist in *either* profiles or escort_profiles table
  const canPartnerProceed = partnerStatus.existsInProfiles || partnerStatus.existsInEscortProfiles;

  if (!canUserProceed || !canPartnerProceed) {
    console.error("Failed to ensure profiles exist for conversation start", { userId, userStatus, partnerId, partnerStatus });
    // Optionally throw an error or return null/specific error indicator
    return null; 
  }

  // 2. Check for existing conversation
  const { data: existing, error: existingError } = await supabase
    .from('conversations')
    .select('*')
    .or(`and(participant1_id.eq.${userId},participant2_id.eq.${partnerId}),and(participant1_id.eq.${partnerId},participant2_id.eq.${userId})`)
    .maybeSingle();

  if (existingError) {
    console.error('Error checking for existing conversation:', existingError);
    throw existingError;
  }

  let conversationToReturnId: string | null = null;

  if (existing) {
    console.log('Conversation already exists:', existing.id);
    conversationToReturnId = existing.id;
  } else {
    // Create new conversation
    console.log('Creating new conversation between', userId, 'and', partnerId);
    const { data: newConversationData, error: insertError } = await supabase
      .from('conversations')
      .insert({ participant1_id: userId, participant2_id: partnerId, status: 'active' })
      .select('id') // Only select the ID
      .single();

    if (insertError) {
      console.error('Error creating new conversation:', insertError);
      throw insertError;
    }
    if (!newConversationData) {
      console.error('Failed to create conversation, no data returned');
      return null;
    }
    
    console.log('New conversation created:', newConversationData.id);
    conversationToReturnId = newConversationData.id;
  }

  if (!conversationToReturnId) return null;

  // Fetch the single conversation's full data using the main function
  // This ensures consistency in how conversation objects are structured
  const allConversations = await fetchConversationsData(userId);
  const specificConversation = allConversations.find(c => c.id === conversationToReturnId);
  
  return specificConversation || null; 
};

// Marks messages from a specific partner in a conversation as read
export const markMessagesAsRead = async (userId: string, conversationId: string): Promise<void> => {
  console.log(`Marking messages as read for user ${userId} in conversation ${conversationId}`);
  const { error } = await supabase
    .from('messages')
    .update({ is_read: true })
    .eq('conversation_id', conversationId)
    .eq('recipient_id', userId)
    .eq('is_read', false); // Only update unread messages

  if (error) {
    console.error('Error marking messages as read:', error);
    throw error;
  }
   console.log(`Messages marked as read for conversation ${conversationId}`);
}; 

// Archives a conversation
export const archiveConversation = async (conversationId: string, userId: string): Promise<void> => {
  console.log(`Archiving conversation ${conversationId} for user ${userId}`);
  
  // First, check if the user is a participant in this conversation
  const { data: conversation, error: fetchError } = await supabase
    .from('conversations')
    .select('*')
    .eq('id', conversationId)
    .or(`participant1_id.eq.${userId},participant2_id.eq.${userId}`)
    .maybeSingle();
    
  if (fetchError) {
    console.error('Error fetching conversation for archiving:', fetchError);
    throw fetchError;
  }
  
  if (!conversation) {
    console.error(`User ${userId} is not a participant in conversation ${conversationId}`);
    throw new Error('Vous n\'êtes pas autorisé à archiver cette conversation');
  }
  
  // Update the conversation status to 'archived'
  const { error: updateError } = await supabase
    .from('conversations')
    .update({ status: 'archived' })
    .eq('id', conversationId);
    
  if (updateError) {
    console.error('Error archiving conversation:', updateError);
    throw updateError;
  }
  
  console.log(`Conversation ${conversationId} successfully archived`);
}; 